import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Avatar, Menu, MenuItem, Divider,
} from '@mui/material';
import {
  Menu as MenuIcon, Dashboard as DashboardIcon, Business as ClinicIcon,
  AccountTree as CustomerIcon,
  People as PeopleIcon, LocalHospital as ProviderIcon,   Logout as LogoutIcon,
  SettingsOutlined as ProfileSettingsIcon, PaidOutlined as UsageIcon,
  TollOutlined as CreditIcon, FactCheckOutlined as AuditIcon,
  ScienceOutlined as ProtocolIcon,
  History as HistoryIcon,
  EventNoteOutlined as DemoIcon,
  InsightsOutlined as WebsiteAnalyticsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 272;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  // Above Clinics on purpose: this is the list that shows an account with no
  // clinic in it yet, which the Clinics list structurally cannot.
  { text: 'Customers', icon: <CustomerIcon />, path: '/customers' },
  // "Clinics" was the whole platform's clinic list. It now lists only standalone
  // practices — anything belonging to a customer is on Customers — so the label says so.
  { text: 'Standalone clinics', icon: <ClinicIcon />, path: '/clinics' },
  { text: 'Providers', icon: <ProviderIcon />, path: '/providers' },
  { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
  { text: 'AI Usage & Cost', icon: <UsageIcon />, path: '/usage' },
  { text: 'Protocol Cost', icon: <ProtocolIcon />, path: '/protocol-cost' },
  { text: 'AI Credits', icon: <CreditIcon />, path: '/credits' },
  { text: 'Session history', icon: <HistoryIcon />, path: '/credits/history' },
  { text: 'Audit Trail', icon: <AuditIcon />, path: '/audit' },
  { text: 'Demo requests', icon: <DemoIcon />, path: '/demo-requests' },
  { text: 'Website analytics', icon: <WebsiteAnalyticsIcon />, path: '/website-analytics' },
];

const sidebarBg =
  'linear-gradient(165deg, #0b1220 0%, #1e293b 48%, #0f172a 100%)';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    setAnchorEl(null);
    navigate('/settings');
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: sidebarBg,
        borderRight: '1px solid rgba(201, 162, 39, 0.12)',
      }}
    >
      <Toolbar sx={{ px: 2.5, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #c9a227 0%, #fde68a 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0f172a',
              fontWeight: 800,
              fontSize: 20,
              boxShadow: '0 8px 24px rgba(201, 162, 39, 0.35)',
            }}
          >
            M
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 700,
                color: '#f8fafc',
                lineHeight: 1.15,
              }}
            >
              ScribeLive
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(248, 250, 252, 0.55)', letterSpacing: 0.6 }}>
              Platform Operations
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
      <List sx={{ px: 1.5, py: 2 }}>
        {menuItems.map((item) => {
          const isActive =
            item.path === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  color: 'rgba(248, 250, 252, 0.75)',
                  '&:hover': { bgcolor: 'rgba(201, 162, 39, 0.08)' },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(201, 162, 39, 0.18)',
                    color: '#fef3c7',
                    borderLeft: '3px solid #c9a227',
                    pl: 1.5,
                    '&:hover': { bgcolor: 'rgba(201, 162, 39, 0.22)' },
                    '& .MuiListItemIcon-root': { color: '#fde68a' },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'rgba(255,255,255,0.78)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          color: 'text.primary',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {user?.full_name || user?.name}
            </Typography>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: '#c9a227',
                  color: '#0f172a',
                  fontWeight: 700,
                }}
              >
                {(user?.full_name || user?.name || 'A').charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <ProfileSettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, border: 0, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
          background: `
            radial-gradient(ellipse 90% 55% at 50% -20%, rgba(201, 162, 39, 0.14), transparent 55%),
            radial-gradient(ellipse 70% 60% at 100% 0%, rgba(30, 41, 59, 0.08), transparent 50%),
            linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)
          `,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
